import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Modal,
  Fade,
  Backdrop,
  Checkbox,
  Autocomplete,
  TextField as MuiTextField
} from '@mui/material';
import axios from 'axios';

const CreateExhibitorModal = ({ open, handleClose, productList }) => {
  const [formData, setFormData] = useState({
    productId: '',
    productVariantId: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleEventChange = (event, newValue) => {
    if (newValue) {
      setSelectedEvent(newValue);
      setFormData(prev => ({
        ...prev,
        productId: newValue.ex_product_id || '',
        productVariantId: ''
      }));
    } else {
      setSelectedEvent(null);
      setFormData(prev => ({
        ...prev,
        productId: '',
        productVariantId: ''
      }));
    }
  };

  const handleFileChange = e => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const payload = new FormData();
    if (selectedEvent) {
      payload.append('event_name', selectedEvent.event_name);
    }
    payload.append('productId', Number(formData.productId));
    if (formData.productVariantId) {
      payload.append('productVariantId', formData.productVariantId); // still named this way for backend compatibility
    }
    if (file) {
      payload.append('file', file);
    }

    try {
      const response = await axios.post(
        '/api/v1/exhibitors-list/admin/upload-exhibitors-list-data',
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setMessage(response.data.message || 'Exhibitors list uploaded.');
      handleClose(true);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Upload Exhibitors List
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={productList}
                  getOptionLabel={(option) => option.event_name || ''}
                  value={selectedEvent}
                  onChange={handleEventChange}
                  disableCloseOnSelect
                  renderOption={(props, option) => {
                    const hasVariants = option.product_variants?.length > 0;
                    return (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={selectedEvent?.ex_product_id === option.ex_product_id}
                              tabIndex={-1}
                              disableRipple
                            />
                            <Typography variant="body1">{option.event_name}</Typography>
                          </Box>

                          {hasVariants && (
                            <Box sx={{ pl: 4 }}>
                              {option.product_variants.map((variant) => (
                                <Box
                                  key={variant.variation_id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor:
                                      formData.productVariantId === variant.variation_id
                                        ? '#f0f0f0'
                                        : 'transparent',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    mt: 0.5,
                                    cursor: 'pointer',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData((prev) => ({
                                      ...prev,
                                      productId: option.ex_product_id,
                                      productVariantId: variant.variation_id, // using variation_id
                                    }));
                                    setSelectedEvent(option);
                                  }}
                                >
                                  <Checkbox
                                    checked={formData.productVariantId === variant.variation_id}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <Typography variant="body2">{variant.name || 'Unnamed Variant'}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Select Event Name"
                      placeholder="Search event name"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth>
                  Upload CSV File
                  <input type="file" accept=".csv" hidden onChange={handleFileChange} />
                </Button>
                {file && (
                  <Typography variant="body2" mt={1}>
                    {file.name}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Upload'}
                </Button>
              </Grid>
            </Grid>
            {message && (
              <Typography mt={2} color="secondary">
                {message}
              </Typography>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4
};

export default CreateExhibitorModal;
